import { useCreateCompanyUser } from "@/api/console/companies/useCreateCompanyUser";
import InputGroup from "@/components/InputGroup";
import Select from "@/components/Select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { handleFormErrors } from "@/lib/handleFormErrors";
import toast from "@/lib/toast";
import {
  CompanyUserForm,
  companyUserFormSchema,
} from "@/schema/console/companies/CompanyUser";
import useMemberDialog from "@/src/console/(auth)/companies/[id]/_hooks/useMemberDialog";
import { SpinnerIosRegular } from "@fluentui/react-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";

interface AddMemberDialogProps {
  companyId: number;
}

function AddMemberDialog({ companyId }: AddMemberDialogProps) {
  const { t } = useTranslation();
  const { type, resetDialog } = useMemberDialog();

  const methods = useForm<CompanyUserForm>({
    resolver: zodResolver(companyUserFormSchema),
    defaultValues: {
      role: 1,
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    register,
    setError,
  } = methods;

  console.log(errors);

  const { field: roleField } = useController({
    control,
    name: "role",
  });

  const { trigger: createCompanyUser, isMutating } = useCreateCompanyUser({
    onSuccess: (data) => {
      resetDialog();
      toast.success(data.message);
      mutate(`/console/companies/${companyId}`);
    },
    onError: (error) => {
      handleFormErrors(error.errors, setError);
    },
  });

  return (
    <Dialog
      open={type === "add"}
      onOpenChange={(open) => !open && resetDialog()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("add-member")}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit((data) => {
              createCompanyUser({
                ...data,
                company_id: companyId,
              });
            })();
          }}
        >
          <div className="grid grid-cols-2 gap-6 px-6 pb-5 pt-4">
            <InputGroup
              label={t("name")}
              required
              error={errors.name?.message}
              register={register("name", {
                required: t("name-required"),
              })}
            />
            <InputGroup
              label={t("name-kana")}
              // error={errors.name?.message}
              register={register("name_kana")}
            />
            <InputGroup
              className="col-span-full"
              label={t("email")}
              type="email"
              required
              error={errors.name?.message}
              register={register("email", {
                required: t("email-required"),
              })}
            />
            <InputGroup
              className="col-span-full"
              label={t("phone")}
              type="text"
              register={register("phone_number")}
            />
            <div className="col-span-full">
              <div className="mb-1">{t("role")}</div>
              <Select
                placeholder="Select role"
                className="col-span-2 rounded-md border"
                value={`${roleField.value}`}
                onChange={(value) => roleField.onChange(parseInt(value))}
                options={[
                  { value: "0", label: t("admin") },
                  { value: "1", label: t("member") },
                ]}
              />
              {errors.role?.message && (
                <p className="text-red-500">{errors.role.message}</p>
              )}
            </div>
            <InputGroup
              label={t("position")}
              type="text"
              register={register("position")}
            />
            <InputGroup
              label={t("department")}
              type="text"
              register={register("department")}
            />
            <InputGroup
              label={t("password")}
              type="password"
              required
              className="col-span-full"
              error={errors.password?.message}
              register={register("password", {
                required: t("password-required"),
              })}
            />
            <Button
              className="col-span-full"
              LoadingComponent={<SpinnerIosRegular className="animate-spin" />}
              isLoading={isMutating}
              type="submit"
              disabled={!isDirty}
            >
              {t("submit")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddMemberDialog;
